/**
 * Inline "what is the agent doing right now" chips.
 *
 * Rendered as a single right-aligned strip directly above the
 * :class:`AgentInput` compose surface, so live session metadata sits
 * one glance away from the textarea without growing a second status
 * bar.
 *
 * Two tiny components, one file:
 *
 *   - :func:`ActivityChips` — live SDK work: SHELLS, MONITORS, AGENTS
 *     counts and the current ``TodoWrite`` summary. Task / todo chips
 *     open hover popovers anchored on themselves.
 *   - :func:`HealthChips` — current context-window utilization
 *     (with a tooltip showing ``used / max`` token counts). Cost and
 *     rate-limit signals are intentionally not surfaced — the
 *     existing top-of-overlay error banner already handles a
 *     rejected rate limit, and an "approaching the limit" pill just
 *     makes users anxious without giving them anything actionable.
 *
 * Both components return ``null`` when their slice has no real signal
 * yet. The reducer hydrates context usage from REST history, so a
 * resumed conversation should not sit at a fake 0% waiting for the
 * next live model message.
 *
 * Popovers open ``side="top"`` because the chips sit at the bottom
 * of the panel — opening downward would clip against the viewport
 * edge or, worse, the OS chrome.
 */

import { ArrowDown, Bot, Check, Gauge, ListTodo, Square, Terminal, TerminalSquare, X } from 'lucide-react';
import type * as React from 'react';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import type { ActiveTask, ActivityState, AgentPanelState, PlanState, TodoItem } from '@/lib/agent/reducer';
import { latestContextTokens, pendingMonitors } from '@/lib/agent/reducer';
import { cn } from '@/lib/utils/cn';

export interface ActivityChipsProps {
  state: AgentPanelState;
  /**
   * Terminate a live SDK background task by id. When set, the
   * task popovers render a per-row stop control anywhere the row has
   * a real SDK task id.
   */
  onStopTask?: (taskId: string) => void;
  className?: string;
}

/**
 * Activity cluster: live SDK tasks (shells / monitors / agents)
 * and the current ``TodoWrite`` checklist. Renders nothing when the
 * slice is empty so the strip stays invisible on a quiet session.
 */
export function ActivityChips({ state, onStopTask, className }: ActivityChipsProps) {
  const { activity } = state;
  // Merge real ``monitor``-category active tasks (from
  // ``task_started``) with synthesized entries inferred from
  // pending ``Monitor`` tool_use blocks. The chip + popover see one
  // unified list so the strip surfaces every live watcher, even
  // when the SDK side-channel envelope didn't fire.
  const monitorTasks = _allMonitorTasks(state);
  if (!_hasAnythingToShow(state)) return null;
  return (
    <div
      className={cn(
        'flex min-w-0 shrink items-center justify-end gap-1.5 overflow-hidden whitespace-nowrap',
        className
      )}
    >
      {activity.bashCount > 0 && (
        <CountChip
          noun="shell"
          count={activity.bashCount}
          icon={<Terminal className="size-3" />}
          accent="amber"
          popover={
            <ActivityTasksPopover
              icon={<TerminalSquare className="size-3.5" />}
              accent="amber"
              title="Background shells"
              tasks={_filterTasks(activity, 'bash')}
              onStopTask={onStopTask}
              stopLabel="Kill"
            />
          }
        />
      )}
      {monitorTasks.length > 0 && (
        <CountChip
          noun="monitor"
          count={monitorTasks.length}
          icon={<Square className="size-3" />}
          accent="violet"
          popover={
            <ActivityTasksPopover
              icon={<Square className="size-3.5" />}
              accent="violet"
              title="Active monitors"
              tasks={monitorTasks}
              canStopTask={_isRealSdkTask}
              onStopTask={onStopTask}
              stopLabel="Stop"
              stopIconOnly
            />
          }
        />
      )}
      {activity.agentCount > 0 && (
        <CountChip
          noun="subagent"
          count={activity.agentCount}
          icon={<Bot className="size-3" />}
          accent="gray"
          popover={
            <ActivityTasksPopover
              icon={<Bot className="size-3.5" />}
              accent="gray"
              title="Active subagents"
              tasks={_filterTasks(activity, 'agent')}
              onStopTask={onStopTask}
              stopLabel="Stop"
              stopIconOnly
            />
          }
        />
      )}
      {activity.plan && <PlanChip plan={activity.plan} />}
    </div>
  );
}

export interface HealthChipsProps {
  state: AgentPanelState;
  className?: string;
}

/**
 * Optional health chips: just the current context-window utilization
 * (with a tooltip showing ``used / max`` token counts).
 *
 * Rate-limit signals deliberately don't surface here — the
 * existing top-of-overlay error banner already covers the rejected
 * case loudly, and an "Approaching rate limit" pill in the input
 * strip just makes users anxious without giving them anything to
 * act on.
 */
export function HealthChips({ state, className }: HealthChipsProps) {
  const context = _contextUsage(state);
  if (!context) return null;
  return (
    <div className={cn('flex shrink-0 items-center gap-1.5', className)}>
      <ContextChip usage={context} />
    </div>
  );
}

/**
 * Whether the floating chip rail should render activity or health
 * metadata. A quiet brand-new session has no health signal yet, but a
 * hydrated conversation should have nonzero context usage from the
 * server's full-transcript session snapshot before this renders.
 */
export function hasMetadataToShow(state: AgentPanelState): boolean {
  return _hasAnythingToShow(state) || _contextUsage(state) !== null;
}

function _hasAnythingToShow(state: AgentPanelState): boolean {
  const { activity } = state;
  if (activity.bashCount > 0 || activity.agentCount > 0 || activity.plan !== null) return true;
  return _allMonitorTasks(state).length > 0;
}

/**
 * Merge real ``monitor``-category entries from
 * :attr:`AgentPanelState.activity.activeTasks` with synthesized
 * entries from :func:`pendingMonitors`. The :func:`pendingMonitors`
 * selector already filters out tool_uses that have a paired live
 * SDK task, so there are no duplicates -- the two lists are
 * disjoint by construction.
 */
function _allMonitorTasks(state: AgentPanelState): ActiveTask[] {
  const real = _filterTasks(state.activity, 'monitor');
  const synthesized = pendingMonitors(state);
  if (synthesized.length === 0) return real;
  return [...real, ...synthesized];
}

/**
 * Synthesized monitor entries carry the ``synth-monitor:`` prefix
 * and aren't known to the server. They stay visible in the monitor
 * popover, but their close button is hidden until a real SDK task id
 * arrives via ``task_started`` or the background-start tool_result.
 */
function _isRealSdkTask(task: ActiveTask): boolean {
  return !task.taskId.startsWith('synth-monitor:');
}

// =============================================================================
// Context-window utilization
// =============================================================================

// Opus 4.8 always runs the 1M context window (the workspace agent
// server pins ``claude-opus-4-8[1m]`` via ``--model`` on every SDK
// spawn), so the context-fill chip's denominator is a fixed constant.
const AGENT_CONTEXT_WINDOW_TOKENS = 1_000_000;

interface ContextUsage {
  used: number;
  max: number;
  ratio: number;
}

function _contextUsage(state: AgentPanelState): ContextUsage | null {
  // Show the *latest* per-iteration prompt size -- what Claude Code's
  // own ``/context`` reports and what the chip's tooltip describes
  // ("the conversation, agent baseline, and any files the agent has
  // read so far"). We previously showed the running high-water mark
  // for stability, but that caused the chip to appear "stuck" when
  // the conversation plateaued -- the user couldn't tell whether
  // their context was still growing, holding, or actually smaller
  // after a compact.
  //
  // ``latestContextTokens`` walks the most recent assistant turn with
  // usage matching the current ``compactEpoch``, so values from
  // pre-compact turns never leak in. We fall back to the high water
  // when no assistant turn yet carries usage (fresh attach pulling
  // in REST history before any live frame lands), so a resumed chat
  // doesn't sit at 0% until the next message.
  const used = latestContextTokens(state) ?? state.contextTokensHighWater;
  if (used <= 0) return null;
  const max = AGENT_CONTEXT_WINDOW_TOKENS;
  return { used, max, ratio: Math.min(1, used / max) };
}

function _filterTasks(activity: ActivityState, category: ActiveTask['category']): ActiveTask[] {
  return Object.values(activity.activeTasks)
    .filter((t) => t.category === category)
    .sort((a, b) => a.startedAt - b.startedAt);
}

// =============================================================================
// Chip primitives
// =============================================================================

type ChipAccent = 'amber' | 'sky' | 'violet' | 'emerald' | 'indigo' | 'gray' | 'red';

/**
 * Shared pill-chip class for every status / action chip in the
 * strip above the input — used by every status chip in this file
 * plus the inline jump-to-latest control further down.
 */
function chipClass(accent: ChipAccent = 'gray'): string {
  return cn(_chipBase(), _chipAccent(accent));
}

function _chipBase(): string {
  return cn(
    'inline-flex shrink-0 select-none items-center gap-1 rounded-full px-2.5 py-1',
    'font-medium text-[12px] leading-[1.2] transition-colors',
    _chipShadow(),
    'focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-300 dark:focus-visible:ring-white/20'
  );
}

function _chipShadow(): string {
  return 'shadow-[0_8px_18px_-12px_rgba(15,23,42,0.26)] dark:shadow-[0_10px_22px_-14px_rgba(0,0,0,0.48)]';
}

function _chipAccent(accent: ChipAccent): string {
  switch (accent) {
    case 'amber':
      return 'bg-amber-50 text-amber-800 hover:bg-amber-100 dark:bg-amber-500/10 dark:text-amber-300 dark:hover:bg-amber-500/15';
    case 'sky':
      return 'bg-sky-50 text-sky-800 hover:bg-sky-100 dark:bg-sky-500/10 dark:text-sky-300 dark:hover:bg-sky-500/15';
    case 'violet':
      return 'bg-violet-50 text-violet-800 hover:bg-violet-100 dark:bg-violet-500/10 dark:text-violet-300 dark:hover:bg-violet-500/15';
    case 'emerald':
      return 'bg-emerald-50 text-emerald-800 hover:bg-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-300 dark:hover:bg-emerald-500/15';
    case 'indigo':
      return 'bg-indigo-50 text-indigo-800 hover:bg-indigo-100 dark:bg-indigo-500/10 dark:text-indigo-300 dark:hover:bg-indigo-500/15';
    case 'red':
      return 'bg-red-50 text-red-800 hover:bg-red-100 dark:bg-red-500/10 dark:text-red-300 dark:hover:bg-red-500/15';
    default:
      return 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-white/5 dark:text-gray-300 dark:hover:bg-white/10';
  }
}

interface CountChipProps {
  /**
   * Singular form of the noun this chip counts ("shell", "monitor",
   * "subagent"). Pluralized automatically based on :attr:`count` so
   * the label reads "1 monitor" / "3 monitors" — same casual,
   * sentence-case voice as :class:`PlanChip`'s "0/3 todos".
   */
  noun: string;
  count: number;
  icon: React.ReactNode;
  accent: ChipAccent;
  popover: React.ReactNode;
}

/**
 * Activity count chip — same hover pattern as :class:`PlanChip`.
 * Hovering reveals the live tasks in this category; the popover body
 * decides whether those rows also expose stop controls.
 */
function CountChip({ noun, count, icon, accent, popover }: CountChipProps) {
  const label = `${count} ${count === 1 ? noun : `${noun}s`}`;
  return (
    <ChipHoverCard
      trigger={
        <span className={cn(_chipBase(), _chipAccent(accent), 'cursor-default')}>
          {icon}
          <span className="tabular-nums">{label}</span>
        </span>
      }
    >
      {popover}
    </ChipHoverCard>
  );
}

/**
 * Shared HoverCard wrapper used by every chip popover so the trigger
 * timing, anchor, and shell match across the strip — open delay,
 * close delay, side, alignment, max-height all live here.
 */
function ChipHoverCard({ trigger, children }: { trigger: React.ReactNode; children: React.ReactNode }) {
  return (
    <HoverCard openDelay={120} closeDelay={80}>
      <HoverCardTrigger asChild>{trigger}</HoverCardTrigger>
      <HoverCardContent
        side="top"
        align="end"
        sideOffset={10}
        className={cn('w-[320px] overflow-hidden p-0', _POPOVER_SHELL)}
      >
        {children}
      </HoverCardContent>
    </HoverCard>
  );
}

// =============================================================================
// Plan chip
// =============================================================================

function PlanChip({ plan }: { plan: PlanState }) {
  const summary = _planSummary(plan);
  const current = plan.current ? (plan.current.activeForm ?? plan.current.content) : null;
  // The plan chip exposes the full checklist on hover (no click to
  // open / dismiss). HoverCard is the right primitive for this:
  // unlike Popover it has no caret affordance, opens on pointer
  // enter with a small delay so it doesn't fire mid-mouse-cross,
  // and closes immediately on leave so it never traps the cursor.
  return (
    <ChipHoverCard
      trigger={
        <span
          // No ``title=`` here — the HoverCard already surfaces the
          // full checklist on hover, and the browser's native
          // tooltip would race against it and double-render.
          className={cn(_chipBase(), _chipAccent('indigo'), 'max-w-[300px] cursor-default')}
        >
          <ListTodo className="size-3 shrink-0" />
          <span className="shrink-0 tabular-nums">{summary}</span>
          {current && (
            <>
              <span aria-hidden className="shrink-0 opacity-40">
                ·
              </span>
              <span className="min-w-0 max-w-[160px] truncate opacity-80">{current}</span>
            </>
          )}
        </span>
      }
    >
      <PlanPopover plan={plan} />
    </ChipHoverCard>
  );
}

function _planSummary(plan: PlanState): string {
  if (plan.total === 0) return 'Empty checklist';
  const noun = plan.kind === 'task' ? (plan.total === 1 ? 'task' : 'tasks') : plan.total === 1 ? 'todo' : 'todos';
  if (plan.completed === plan.total) return `All ${noun} done`;
  return `${plan.completed}/${plan.total} ${noun}`;
}

// =============================================================================
// Popover bodies
// =============================================================================

/**
 * Shared shell classes for every chip hovercard so todos, monitors,
 * shells, subagents, and the context-window chip all read as one
 * family. Captures the rounded border, soft shadow, ring, and
 * dark-mode surface; the actual content + accent comes from
 * :class:`PopoverHeader` and the section bodies below.
 */
const _POPOVER_SHELL = cn(
  'rounded-xl border border-gray-200/80 bg-white shadow-[0_18px_44px_-26px_rgba(15,23,42,0.32)]',
  'ring-1 ring-black/[0.02]',
  'dark:border-white/10 dark:bg-[#15181c] dark:shadow-[0_22px_48px_-22px_rgba(0,0,0,0.62)] dark:ring-white/[0.03]'
);

interface PopoverHeaderProps {
  icon: React.ReactNode;
  accent: ChipAccent;
  title: string;
  /** Right-aligned status — count, percentage, "all done", etc. */
  status?: React.ReactNode;
}

/**
 * Tight, one-line hovercard header. Accent surface holds the icon
 * on the left, a small title in the middle, and right-aligned
 * status (count, percent) on the right. Matches the chip family so
 * the visual jump from chip → popover stays light.
 */
function PopoverHeader({ icon, accent, title, status }: PopoverHeaderProps) {
  return (
    <div className="flex select-none items-center gap-2.5 border-gray-100 border-b px-3 py-2 dark:border-white/[0.06]">
      <span className={cn('flex size-6 shrink-0 items-center justify-center rounded-md', _accentSurface(accent))}>
        {icon}
      </span>
      <div className="min-w-0 flex-1 truncate font-medium text-[12.5px] text-gray-900 leading-none dark:text-gray-100">
        {title}
      </div>
      {status !== undefined && status !== null && (
        <div className="shrink-0 text-[11px] text-gray-400 tabular-nums leading-none dark:text-gray-500">{status}</div>
      )}
    </div>
  );
}

/** Filled accent surface for the popover-header icon badge. */
function _accentSurface(accent: ChipAccent): string {
  switch (accent) {
    case 'amber':
      return 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300';
    case 'sky':
      return 'bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300';
    case 'violet':
      return 'bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300';
    case 'emerald':
      return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300';
    case 'indigo':
      return 'bg-indigo-100 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300';
    case 'red':
      return 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300';
    default:
      return 'bg-gray-100 text-gray-700 dark:bg-white/10 dark:text-gray-200';
  }
}

interface ActivityTasksPopoverProps {
  icon: React.ReactNode;
  accent: ChipAccent;
  title: string;
  tasks: ActiveTask[];
  /** Optional per-row guard for synthetic entries that cannot be stopped. */
  canStopTask?: (task: ActiveTask) => boolean;
  /** When set, each row renders a small stop button on the right. */
  onStopTask?: (taskId: string) => void;
  /** Verb for the per-row stop button — "Kill" for shells, "Stop" else. */
  stopLabel?: string;
  /** Render the stop action as a compact close "x" instead of text. */
  stopIconOnly?: boolean;
}

/**
 * Hovercard listing the live tasks in one category (shells /
 * monitors / subagents). One row per task: a tiny pulsing dot, the
 * description, an inline elapsed timer on the right, then a single
 * dim meta line for the last tool name / token count when present.
 * Shells use the text stop button; monitors and subagents use a
 * compact close "x" in the chip popover. Synthetic monitor rows have
 * no SDK task id, so their close action is omitted until the real id
 * arrives.
 */
export function ActivityTasksPopover({
  icon,
  accent,
  title,
  tasks,
  canStopTask,
  onStopTask,
  stopLabel,
  stopIconOnly,
}: ActivityTasksPopoverProps) {
  return (
    <div className="flex max-h-[60vh] flex-col">
      <PopoverHeader icon={icon} accent={accent} title={title} status={`${tasks.length} active`} />
      <ul className="flex-1 divide-y divide-gray-100/70 overflow-auto dark:divide-white/[0.05]">
        {tasks.length === 0 && (
          <li className="px-3 py-2 text-[12px] text-gray-500 dark:text-gray-400">No active tasks</li>
        )}
        {tasks.map((task) => (
          <TaskRow
            key={task.taskId}
            task={task}
            accent={accent}
            canStopTask={canStopTask}
            onStopTask={onStopTask}
            stopLabel={stopLabel}
            stopIconOnly={stopIconOnly}
          />
        ))}
      </ul>
    </div>
  );
}

function TaskRow({
  task,
  accent,
  canStopTask,
  onStopTask,
  stopLabel,
  stopIconOnly,
}: {
  task: ActiveTask;
  accent: ChipAccent;
  canStopTask?: (task: ActiveTask) => boolean;
  onStopTask?: (taskId: string) => void;
  stopLabel?: string;
  stopIconOnly?: boolean;
}) {
  const elapsed = _fmtMs(task.usage?.duration_ms ?? Math.max(0, Date.now() - task.startedAt));
  const meta = _taskMetaLine(task);
  const stopTask = onStopTask && (canStopTask?.(task) ?? true) ? onStopTask : undefined;
  const stopText = stopLabel ?? 'Stop';
  const stopTitle = `${stopText} ${task.description || task.taskId}`;
  return (
    <li className="flex min-w-0 select-none flex-col gap-0.5 px-3 py-2">
      <div className="flex min-w-0 items-center gap-2">
        <span
          className={cn('size-1.5 shrink-0 rounded-full motion-safe:animate-pulse', _accentDot(accent))}
          aria-hidden
        />
        <span className="min-w-0 flex-1 truncate font-mono text-[12px] text-gray-800 leading-snug dark:text-gray-100">
          {_taskActivity(task)}
        </span>
        <span className="shrink-0 text-[11px] text-gray-400 tabular-nums leading-snug dark:text-gray-500">
          {elapsed}
        </span>
        {stopTask && (
          <button
            type="button"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              stopTask(task.taskId);
            }}
            aria-label={stopTitle}
            title={stopTitle}
            className={cn(
              'inline-flex h-5 shrink-0 cursor-pointer select-none items-center border font-medium text-[10.5px] leading-none transition-colors',
              stopIconOnly ? 'w-5 justify-center rounded-full p-0' : 'gap-1 rounded-md px-1.5',
              'border-gray-200 text-gray-500 hover:border-red-300 hover:bg-red-50 hover:text-red-700',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400/30',
              'dark:border-white/10 dark:text-gray-400 dark:hover:border-red-500/30 dark:hover:bg-red-500/10 dark:hover:text-red-300'
            )}
          >
            <X className={stopIconOnly ? 'size-3' : 'size-2.5'} />
            {!stopIconOnly && <span>{stopText}</span>}
          </button>
        )}
      </div>
      {meta && (
        <div className="truncate pl-3.5 text-[10.5px] text-gray-400 leading-snug dark:text-gray-500">{meta}</div>
      )}
    </li>
  );
}

function _taskActivity(task: ActiveTask): string {
  const description = task.description.trim();
  return description || task.taskId;
}

function _taskMetaLine(task: ActiveTask): string | null {
  const parts: string[] = [];
  if (task.lastToolName) parts.push(task.lastToolName);
  const tools = task.usage?.tool_uses;
  if (tools !== undefined) parts.push(`${tools} ${tools === 1 ? 'tool call' : 'tool calls'}`);
  const tokens = task.usage?.total_tokens;
  if (tokens !== undefined) parts.push(`${_fmtTokens(tokens)} tok`);
  return parts.length > 0 ? parts.join(' · ') : null;
}

/** Tint for the per-row pulsing status dot. Matches the chip family. */
function _accentDot(accent: ChipAccent): string {
  switch (accent) {
    case 'amber':
      return 'bg-amber-500 dark:bg-amber-400';
    case 'violet':
      return 'bg-violet-500 dark:bg-violet-400';
    case 'emerald':
      return 'bg-emerald-500 dark:bg-emerald-400';
    case 'indigo':
      return 'bg-indigo-500 dark:bg-indigo-400';
    case 'sky':
      return 'bg-sky-500 dark:bg-sky-400';
    case 'red':
      return 'bg-red-500 dark:bg-red-400';
    default:
      return 'bg-gray-400 dark:bg-gray-300';
  }
}

function PlanPopover({ plan }: { plan: PlanState }) {
  return (
    <div className="flex max-h-[60vh] flex-col">
      <PopoverHeader
        icon={<ListTodo className="size-3.5" />}
        accent="indigo"
        title={plan.kind === 'task' ? 'Current tasks' : 'Current checklist'}
        status={_planSummary(plan)}
      />
      <ul className="flex-1 divide-y divide-gray-100/70 overflow-auto dark:divide-white/[0.05]">
        {plan.todos.map((todo, i) => {
          const text = todo.activeForm && todo.status === 'in_progress' ? todo.activeForm : todo.content;
          const meta = _taskPlanMeta(todo);
          return (
            <li key={`${i}-${todo.id ?? todo.content}`} className="flex items-start gap-2.5 px-3 py-2">
              <span className="mt-0.5 flex shrink-0 items-center justify-center">{_todoMarker(todo)}</span>
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span
                  className={cn(
                    // Pending text matches the header tone so the
                    // popover reads as one calm gray family rather
                    // than a header + a wall of near-black body text.
                    'text-[12.5px] text-gray-700 leading-snug dark:text-gray-300',
                    todo.status === 'completed' && 'text-gray-400 line-through dark:text-gray-500',
                    todo.status === 'in_progress' && 'font-medium text-gray-900 dark:text-gray-100',
                    (todo.status === 'cancelled' || todo.status === 'deleted') &&
                      'text-gray-400 italic line-through dark:text-gray-600'
                  )}
                >
                  {text}
                </span>
                {meta && (
                  <span className="truncate text-[10.5px] text-gray-400 leading-snug dark:text-gray-500">{meta}</span>
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function _taskPlanMeta(todo: TodoItem): string | null {
  const parts: string[] = [];
  if (todo.owner) parts.push(todo.owner);
  if (todo.blockedBy && todo.blockedBy.length > 0) parts.push(`blocked by ${todo.blockedBy.join(', ')}`);
  if (todo.blocks && todo.blocks.length > 0)
    parts.push(`${todo.blocks.length} ${todo.blocks.length === 1 ? 'block' : 'blocks'}`);
  return parts.length > 0 ? parts.join(' · ') : null;
}

function _todoMarker(todo: TodoItem): React.ReactNode {
  if (todo.status === 'completed') {
    return (
      <span className="flex size-3.5 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-500/20">
        <Check className="size-2 text-emerald-700 dark:text-emerald-300" strokeWidth={3} />
      </span>
    );
  }
  if (todo.status === 'in_progress') {
    return (
      <span className="block size-3.5 rounded-full border-2 border-sky-500 border-dashed bg-sky-100 dark:bg-sky-500/20" />
    );
  }
  if (todo.status === 'cancelled' || todo.status === 'deleted') {
    return <span className="block size-3.5 rounded-full bg-gray-200 opacity-60 dark:bg-white/10" />;
  }
  return <span className="block size-3.5 rounded-full border border-gray-300 dark:border-white/20" />;
}

function _fmtMs(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const minutes = Math.floor(ms / 60_000);
  const seconds = Math.floor((ms % 60_000) / 1000);
  return `${minutes}m${seconds}s`;
}

// =============================================================================
// Action chips
// =============================================================================

export interface JumpToLatestChipProps {
  onClick: () => void;
  className?: string;
}

/**
 * Pill-shaped action chip that scrolls the chat back to the latest
 * message. The parent floats it absolutely over the bottom-right of
 * the chat scroll area instead of placing it inline above the input —
 * an inline position would make the chip's appearance change the
 * scroll viewport's height as the user crosses the at-bottom
 * boundary, which the browser resolves by clamping ``scrollTop``
 * and the chat visibly jerks.
 */
export function JumpToLatestChip({ onClick, className }: JumpToLatestChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      title="Scroll to latest"
      className={cn(chipClass('gray'), 'cursor-pointer', className)}
    >
      <ArrowDown className="size-3 opacity-80" />
      <span>Latest</span>
    </button>
  );
}

// =============================================================================
// Health chip components
// =============================================================================

function ContextChip({ usage }: { usage: ContextUsage }) {
  // Defensive cap: a single Anthropic API call can never exceed
  // the model's context window — if our high-water computation
  // ever produces something north of 100% (it shouldn't, but
  // multi-iteration aggregation has bitten us before) we'd
  // rather show a clamped 100% than scream "126% full" at the
  // user. Keep ``usage.used`` raw in the popover so engineers
  // can still see the real number on hover.
  const ratio = Math.min(1, usage.ratio);
  const pct = Math.round(ratio * 100);
  const pctLabel = pct === 0 ? '<1%' : `${pct}%`;
  const fillPct = pct === 0 ? 1 : pct;
  const accent: ChipAccent = ratio >= 0.9 ? 'red' : ratio >= 0.7 ? 'amber' : 'gray';
  const fillTone = _contextFillTone(accent);
  return (
    <ChipHoverCard
      trigger={
        <span
          className={cn(
            'inline-flex shrink-0 cursor-default select-none items-center gap-1 rounded-full border px-2.5 py-1',
            'font-medium text-[12px] leading-[1.2] backdrop-blur-md transition-colors',
            _chipShadow(),
            'border-gray-200/80 bg-white/85 text-gray-600 hover:border-gray-300/90 hover:bg-gray-50 dark:border-white/10 dark:bg-[#14171b]/85 dark:text-gray-300 dark:hover:border-white/15 dark:hover:bg-white/[0.08]',
            accent === 'amber' &&
              'border-amber-200/80 bg-amber-50/90 text-amber-800 hover:border-amber-300/90 hover:bg-amber-100 dark:border-amber-400/25 dark:bg-amber-500/10 dark:text-amber-300 dark:hover:border-amber-400/35 dark:hover:bg-amber-500/15',
            accent === 'red' &&
              'border-red-200/80 bg-red-50/90 text-red-800 hover:border-red-300/90 hover:bg-red-100 dark:border-red-400/25 dark:bg-red-500/10 dark:text-red-300 dark:hover:border-red-400/35 dark:hover:bg-red-500/15'
          )}
        >
          <Gauge className="size-3 opacity-80" />
          <span className="tabular-nums">{pctLabel}</span>
          <span aria-hidden className="ml-0.5 h-1 w-5 overflow-hidden rounded-full bg-gray-200/80 dark:bg-white/10">
            <span
              className={cn('block h-full rounded-full transition-[width] duration-200', fillTone)}
              style={{ width: `${fillPct}%` }}
            />
          </span>
        </span>
      }
    >
      <ContextPopover usage={usage} pct={pct} pctLabel={pctLabel} accent={accent} fillTone={fillTone} />
    </ChipHoverCard>
  );
}

function ContextPopover({
  usage,
  pct,
  pctLabel,
  accent,
  fillTone,
}: {
  usage: ContextUsage;
  pct: number;
  pctLabel: string;
  accent: ChipAccent;
  fillTone: string;
}) {
  return (
    <div className="flex flex-col">
      <PopoverHeader icon={<Gauge className="size-3.5" />} accent={accent} title="Context window" status={pctLabel} />
      <div className="flex flex-col gap-2.5 px-3 py-2.5">
        {/* Progress bar — visual anchor that matches the chip's
            tiny fill but at full popover width so the user can see
            how close to the wall they are at a glance. */}
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-white/10">
          <div
            className={cn('block h-full rounded-full transition-[width] duration-200', fillTone)}
            style={{ width: `${Math.max(2, pct)}%` }}
          />
        </div>
        <div className="flex items-baseline justify-between font-mono text-[11px] tabular-nums">
          <span className="text-gray-700 dark:text-gray-200">
            {_fmtTokens(usage.used)} <span className="text-gray-400 dark:text-gray-500">used</span>
          </span>
          <span className="text-gray-400 dark:text-gray-500">
            <span className="text-gray-700 dark:text-gray-200">{_fmtTokens(usage.max)}</span> total
          </span>
        </div>
        <div className="text-[11px] text-gray-500 leading-snug dark:text-gray-400">
          The conversation, agent baseline, and any files the agent has read so far. Older turns get summarized when
          this fills up.
        </div>
      </div>
    </div>
  );
}

function _contextFillTone(accent: ChipAccent): string {
  if (accent === 'red') return 'bg-red-500';
  if (accent === 'amber') return 'bg-amber-500';
  return 'bg-gray-500';
}

function _fmtTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 10_000) return `${Math.round(n / 1000)}k`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return n.toLocaleString('en-US');
}
